import React, { useState } from "react";
import { motion } from "framer-motion";
import { Save, ArrowLeft, AlertCircle } from "lucide-react";
import DashboardLayout from './DashboardLayout/DasboardLayout';
import { API_URL } from "./config";
import { useNavigate } from "react-router-dom";

const AddConfig = () => {
    const [controlItem, setControlItem] = useState("");
    const [value, setValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const controlItems = [
        "In Trip Location Sync Interval",
        "New Trip Location Sync Interval",
        "New Driver Free Interval",
        "Last Support Date Previous Version",
        "APK Version Name",
        "Latest Update Available Feature 1",
        "Latest Update Available Feature 2",
        "Latest Update Available Feature 3",
        "Latest APK Download Link (URL)"
    ];

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        const postData = {
            control_item: controlItem,
            value,
        };

        try {
            const response = await fetch(`${API_URL}/application_configs`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(postData),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            // Success Animation and Redirect
            setIsLoading(false);
            showNotification('success', 'Configuration Added Successfully!');
            setTimeout(() => navigate("/Configurations"), 1500);

        } catch (error) {
            setIsLoading(false);
            showNotification('error', 'Failed to add configuration. Please try again.');
        }
    };

    const showNotification = (type, message) => {
        const notification = document.createElement('div');
        notification.className = `fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
            type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white z-50 animate-notification`;
        notification.textContent = message;
        document.body.appendChild(notification);
        setTimeout(() => notification.remove(), 3000);
    };

    return (
        <DashboardLayout>
            <div className="min-h-screen bg-white  p-6">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <button
                            onClick={() => navigate("/Configurations")}
                            className="bg-gradient-to-br from-[#001233] to-[#002966] flex items-center text-gray-300 hover:text-white mb-6 transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 mr-2" />
                            Back to Configurations
                        </button>
                        <h1 className="text-3xl font-bold text-[#002966]">Add New Configuration</h1>
                    </motion.div>

                    {/* Form Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gradient-to-br from-[#001233] to-[#002966] backdrop-blur-lg rounded-xl p-8 border border-white/20 shadow-xl"
                    >
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Control Item Select */}
                            <div>
                                <label className="block text-[#ffc000] font-medium mb-2">
                                    Control Item
                                </label>
                                <select
                                    required
                                    value={controlItem}
                                    onChange={(e) => setControlItem(e.target.value)}
                                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ffc000]/50 transition-all"
                                >
                                    <option value="" className="bg-[#002966]">Select a control item</option>
                                    {controlItems.map((item) => (
                                        <option key={item} value={item} className="bg-[#002966]">
                                            {item}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Value Input */}
                            <div>
                                <label className="block text-[#ffc000] font-medium mb-2">
                                    Value
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={value}
                                    onChange={(e) => setValue(e.target.value)}
                                    placeholder="Enter value"
                                    className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ffc000]/50 transition-all"
                                />
                            </div>

                            {/* Info Note */}
                            <div className="flex items-start space-x-3 bg-white/5 rounded-lg p-4">
                                <AlertCircle className="w-5 h-5 text-[#ffc000] flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-gray-300">
                                    Please ensure the value matches the expected format for the selected control item.
                                    Changes will take effect immediately across the system.
                                </p>
                            </div>

                            {/* Submit Button */}
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                disabled={isLoading}
                                type="submit"
                                className={`w-full flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-all ${
                                    isLoading
                                        ? 'bg-gray-500 cursor-not-allowed'
                                        : 'bg-[#ffc000] hover:bg-[#ffcd2d] text-[#002966]'
                                }`}
                            >
                                <Save className="w-5 h-5 mr-2" />
                                {isLoading ? 'Saving...' : 'Save Configuration'}
                            </motion.button>
                        </form>
                    </motion.div>
                </div>
            </div>

            <style>{`
                @keyframes slideIn {
                    0% { transform: translateY(-100%); opacity: 0; }
                    10% { transform: translateY(0); opacity: 1; }
                    90% { transform: translateY(0); opacity: 1; }
                    100% { transform: translateY(-100%); opacity: 0; }
                }
                .animate-notification {
                    animation: slideIn 3s ease-in-out forwards;
                }
            `}</style>
        </DashboardLayout>
    );
};

export default AddConfig;